import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ToastPosition } from '@this+that/ui';
import log from 'loglevel';

import { UndoToast } from '../../../../components/UndoToast';
import { useCustomToast } from '../../../../hooks/useCustomToast';
import { UNDO_WINDOW_MS } from '../../../../utils/constants/pendingActions';

const TOAST_EXTRA_MS = 500;

export interface UndoableAction {
  key: string;
  message: string;
  commit: () => Promise<void>;
  revert: () => void;
}

export function useTeamUndoAction() {
  const { t } = useTranslation();
  const { customToaster, errorMessage, closeToast } = useCustomToast();
  const actionsRef = useRef<Map<string, UndoableAction>>(new Map());
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const toastIdsRef = useRef<Map<string, string | number>>(new Map());

  const clearEntry = useCallback(
    (key: string) => {
      const timer = timersRef.current.get(key);
      if (timer) {
        clearTimeout(timer);
      }
      timersRef.current.delete(key);
      actionsRef.current.delete(key);
      const toastId = toastIdsRef.current.get(key);
      if (toastId !== undefined) {
        closeToast(toastId);
      }
      toastIdsRef.current.delete(key);
    },
    [closeToast],
  );

  // Held in a ref so both the timed commit and the unmount flush report the
  // same way. The flush runs after the component is gone, so it cannot read a
  // fresh closure — and reverting without telling anyone is how a role change
  // lost on navigation disappeared with no trace.
  const reportFailure = useCallback(
    (action: UndoableAction, err: unknown) => {
      log.error('useTeamUndoAction.commit', err);
      action.revert();
      errorMessage(t('teamHub.undo.error'));
    },
    [errorMessage, t],
  );
  const reportFailureRef = useRef(reportFailure);
  reportFailureRef.current = reportFailure;

  const commit = useCallback(
    async (key: string) => {
      const action = actionsRef.current.get(key);
      clearEntry(key);
      if (!action) {
        return;
      }
      try {
        await action.commit();
      } catch (err) {
        reportFailureRef.current(action, err);
      }
    },
    [clearEntry],
  );

  const undo = useCallback(
    (key: string) => {
      const action = actionsRef.current.get(key);
      clearEntry(key);
      action?.revert();
    },
    [clearEntry],
  );

  const schedule = useCallback(
    (action: UndoableAction) => {
      clearEntry(action.key);
      actionsRef.current.set(action.key, action);
      const timer = setTimeout(() => {
        commit(action.key);
      }, UNDO_WINDOW_MS);
      timersRef.current.set(action.key, timer);
      const toastId = customToaster(
        <UndoToast message={action.message} onUndo={() => undo(action.key)} />,
        ToastPosition.BottomLeft,
        UNDO_WINDOW_MS + TOAST_EXTRA_MS,
      );
      toastIdsRef.current.set(action.key, toastId);
    },
    [clearEntry, commit, customToaster, undo],
  );

  useEffect(() => {
    const actions = actionsRef.current;
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      actions.forEach((action) => {
        action.commit().catch((err) => {
          reportFailureRef.current(action, err);
        });
      });
      actions.clear();
      timers.clear();
    };
  }, []);

  return { schedule };
}
